import React, { useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  useTransGetChainsQuery,
  useTransGetChainsDepoAddrQuery,
  transactionApi,
} from "../../redux/TransactionApi";
import { QRCode } from "react-qrcode-logo";

function DepositModal({ show, onClose, chainType }) {
  const dispatch = useDispatch();
  const { data: chains = [], isLoading } = useTransGetChainsQuery(
    { symbol: chainType },
    { skip: !show }
  );

  const [selectedChain, setSelectedChain] = useState("0"); // string for select value
  const [copySuccess, setCopySuccess] = useState(false);

  const {
    data: chainsAddr,
    isLoading: isLoadingAddr,
    isFetching,
    isSuccess,
    refetch,
  } = useTransGetChainsDepoAddrQuery(
    { chain_id: Number(selectedChain) },
    {
      // skip if no chain selected or modal closed
      skip: !selectedChain || selectedChain === "0" || !show,
      refetchOnMountOrArgChange: true,
    }
  );
  useEffect(() => {
    if (show) {
      setSelectedChain("0");
      setCopySuccess(false);
    }
  }, [show]);

  // helper: extract wallet address from various possible response shapes
  const extractWalletAddress = (payload) => {
    if (!payload) return "";
    if (typeof payload === "string") return payload;
    if (payload.wallet_address) return payload.wallet_address;
    if (payload.data) {
      if (typeof payload.data === "string") return payload.data;
      if (Array.isArray(payload.data) && payload.data.length > 0) {
        const first = payload.data[0];
        if (first?.wallet_address) return first.wallet_address;
        if (first?.address) return first.address;
      }
      if (payload.data.wallet_address) return payload.data.wallet_address;
      if (payload.data.address) return payload.data.address;
    }
    return "";
  };

  // debug: inspect raw payload (optional)
  useEffect(() => {
    if (chainsAddr) console.log("chainsAddr payload:", chainsAddr);
  }, [chainsAddr]);

  // derived single source of truth for the address
  const derivedAddress =
    !selectedChain || selectedChain === "0" || isLoadingAddr || isFetching
      ? ""
      : extractWalletAddress(chainsAddr) || "";

  // reset when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedChain("0");
      setCopySuccess(false);
    }
  }, [show]);

  const copyCodeToClipboard = async () => {
    try {
      if (!derivedAddress) return;
      await navigator.clipboard.writeText(derivedAddress);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedChain("0");
    setCopySuccess(false);
    if (selectedChain && selectedChain !== "0") {
      dispatch(
        transactionApi.util.invalidateTags([
          { type: "DepositAddress", id: Number(selectedChain) },
        ])
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="account-popup-outer"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Deposit</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row align-items-center">
          <div className="col-8">
            <div className="mb-3">
              <label htmlFor="chainSelect">Select Chain:</label>
              <Form.Select
                id="chainSelect"
                className="form-control fs12"
                value={selectedChain}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedChain(value);
                  // force refetch if needed (safe no-op if skip blocks it)
                  refetch?.();
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <option>Loading chains...</option>
                ) : (
                  <>
                    <option value="0">Select Chain</option>
                    {chains?.data?.map((chain) => (
                      <option key={chain.chainId} value={chain.chainId}>
                        {chain.chainName}
                      </option>
                    ))}
                  </>
                )}
              </Form.Select>
            </div>

            <label>Your Wallet Address:</label>
            <div className="link-address position-relative">
              <div className="form-group mb-0">
                <input
                  className="form-control fs12"
                  readOnly
                  name="wallet_address"
                  value={
                    isLoadingAddr || isFetching
                      ? "Loading address..."
                      : derivedAddress || "No address available"
                  }
                />
              </div>
              <div className="copy-btn-outer">
                {copySuccess && (
                  <div style={{ color: "green", fontSize: "0.85rem" }}>
                    Address Copied!
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn_man position-absolute copy_button"
                  onClick={copyCodeToClipboard}
                  disabled={!derivedAddress}
                  aria-label="Copy wallet address"
                >
                  <i className="fa fa-clone" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="col-4 pl-0">
            <div className="tc qr_man">
              {derivedAddress ? (
                <QRCode value={derivedAddress} />
              ) : (
                <div
                  className="qr_blur"
                  style={{
                    background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='90' height='90'%3E%3Crect width='90' height='90' fill='%23ccc'/%3E%3Cpath d='M15 15h10v10H15zM45 15h10v10H45zM75 15h10v10H75zM15 45h10v10H15zM45 45h10v10H45zM75 45h10v10H75zM15 75h10v10H15zM45 75h10v10H45zM75 75h10v10H75z' fill='%23999'/%3E%3C/svg%3E") center/cover no-repeat`,
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="disclaimer-main border-top w-100 mt-4">
          <div className="disclaimer-outer pt-3">
            <h6>
              <i className="fa fa-exclamation-circle" aria-hidden="true"></i>{" "}
              Disclaimer
            </h6>
            <ul>
              <li>
                Send only using the <strong>{selectedChain}</strong> network.
                Using any other network will result in loss of funds.
              </li>
              <li>
                Deposit only <strong>{chainType}</strong> to this deposit
                address. Depositing any other asset will result in a loss of
                funds.
              </li>
            </ul>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default DepositModal;
